import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  BookOpen, 
  PenLine,
  Sparkles,
  ListChecks,
  Wrench,
  Clock,
  CheckCircle,
  Target,
  ArrowRight,
  Hammer,
  ShoppingCart,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function FeatureRecipes() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleCTA = () => {
    navigate(user ? '/recipes' : '/auth');
  };

  return (
    <div className="min-h-screen bg-black-rich pt-20">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Guide Travaux Étape par Étape | Recettes Bricolage | Assistant by Léon</title>
        <meta name="description" content="Obtenez un guide travaux personnalisé avec la liste des outils, matériaux et étapes détaillées. Tutoriels bricolage générés par IA pour réussir vos projets." />
        <meta name="keywords" content="guide travaux, tutoriel bricolage, comment faire travaux, étapes rénovation, liste matériaux, conseils bricolage" />
        <link rel="canonical" href="https://assistantbyleon.fr/fonctionnalite/recettes-travaux" />
        <meta property="og:title" content="Guide Travaux Personnalisé - Recettes Bricolage par Léon" />
        <meta property="og:description" content="Décrivez votre projet et obtenez un guide complet : outils, matériaux, étapes et conseils d'expert." />
        <meta property="og:type" content="website" />
      </Helmet>
      
      {/* Section 1: Accroche SEO optimisée */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6"
          >
            <BookOpen className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-400 font-medium">Guide travaux personnalisé</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl font-heading font-bold text-white mb-6"
          >
            Votre guide bricolage{' '}
            <span className="text-gold-500">étape par étape</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-zinc-400 max-w-2xl mx-auto"
          >
            Décrivez votre projet et recevez un <strong className="text-white">tutoriel complet</strong> avec 
            la <strong className="text-white">liste des outils</strong>, les <strong className="text-white">matériaux à acheter</strong> et 
            toutes les <strong className="text-white">étapes détaillées</strong>.
            <span className="block mt-2 text-gold-400">Même les bricoleurs débutants peuvent réussir.</span>
          </motion.p>
        </div>
      </section>

      {/* Section 2: Comment obtenir un guide travaux */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-heading font-bold text-white text-center mb-4"
          >
            Comment créer votre recette travaux ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-zinc-400 text-center mb-12 max-w-2xl mx-auto"
          >
            Fini les heures de recherche sur les forums. Léon génère un guide adapté à votre projet en quelques secondes.
          </motion.p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                step: '1', 
                title: 'Décrivez votre projet', 
                desc: 'Expliquez simplement ce que vous voulez faire : "poser du parquet", "peindre un mur"...',
                icon: PenLine,
                color: 'emerald'
              },
              { 
                step: '2', 
                title: 'Léon génère le guide', 
                desc: 'L\'IA crée une recette complète adaptée à votre situation.',
                icon: Sparkles,
                color: 'gold'
              },
              { 
                step: '3', 
                title: 'Suivez les étapes', 
                desc: 'Réalisez vos travaux avec une méthode claire et des conseils pro.',
                icon: ListChecks,
                color: 'blue'
              }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-${item.color}-500/10 border border-${item.color}-500/20 flex items-center justify-center`}>
                  <item.icon className={`w-8 h-8 text-${item.color}-400`} />
                </div>
                <div className="text-gold-500 font-bold text-sm mb-2">Étape {item.step}</div>
                <h3 className="text-white font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-zinc-500 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Exemple de recette générée */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-heading font-bold text-white text-center mb-8"
          >
            Exemple : tutoriel pose de parquet flottant
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 border-emerald-500/20"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Wrench className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg mb-1">Demande : "Poser du parquet flottant dans mon salon de 20m²"</h3>
                <p className="text-zinc-500 text-sm">Recette générée par Léon</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Hammer className="w-5 h-5 text-blue-400" />
                  <h4 className="text-white font-semibold">Outils nécessaires</h4>
                </div>
                <p className="text-zinc-400 text-sm">Cutter, maillet en caoutchouc, cales d'espacement, mètre, crayon, scie sauteuse, tire-lame</p>
              </div>
              
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <ShoppingCart className="w-5 h-5 text-emerald-400" />
                  <h4 className="text-white font-semibold">Matériaux à acheter</h4>
                </div>
                <p className="text-zinc-400 text-sm">22m² de parquet (prévoir 10% de marge), sous-couche acoustique, plinthes, barre de seuil</p>
              </div>
              
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <ListChecks className="w-5 h-5 text-gold-400" />
                  <h4 className="text-white font-semibold">8 étapes détaillées</h4>
                </div>
                <ol className="text-zinc-400 text-sm space-y-1 list-decimal list-inside">
                  <li>Préparer le support (nettoyage, vérification planéité)</li>
                  <li>Laisser acclimater le parquet 48h</li>
                  <li>Poser la sous-couche...</li>
                  <li className="text-zinc-500">+ 5 autres étapes avec conseils</li>
                </ol>
              </div>
              
              <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                  <h4 className="text-amber-400 font-semibold">Conseil de Léon</h4>
                </div>
                <p className="text-zinc-300 text-sm">Commencez toujours par le mur le plus long et le plus droit. Utilisez des cales de 8mm pour le joint de dilatation périphérique.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 4: Avantages des recettes travaux */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-heading font-bold text-white text-center mb-4"
          >
            Pourquoi utiliser les recettes travaux de Léon ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-zinc-400 text-center mb-10 max-w-2xl mx-auto"
          >
            Un guide personnalisé vaut mieux que 100 vidéos YouTube génériques.
          </motion.p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: ListChecks, title: 'Liste complète', desc: 'Tous les outils et matériaux nécessaires', color: 'emerald' },
              { icon: Target, title: 'Méthode claire', desc: 'Étapes simples et ordonnées logiquement', color: 'blue' },
              { icon: Clock, title: 'Gain de temps', desc: 'Plus besoin de chercher pendant des heures', color: 'gold' },
              { icon: CheckCircle, title: 'Résultat pro', desc: 'Conseils d\'expert pour éviter les erreurs', color: 'purple' }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card-dark p-5 text-center"
              >
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-${item.color}-500/10 flex items-center justify-center`}>
                  <item.icon className={`w-6 h-6 text-${item.color}-400`} />
                </div>
                <h3 className="text-white font-bold mb-1">{item.title}</h3>
                <p className="text-zinc-500 text-xs">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: CTA Final */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-xl mx-auto text-center"
        >
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-4">
            Prêt à créer votre guide travaux ?
          </h2>
          <p className="text-zinc-400 mb-8">
            Décrivez votre projet et obtenez un tutoriel complet en quelques secondes.
            <span className="block text-gold-400 mt-2">Gratuit pour votre première recette.</span>
          </p>
          
          <Button
            onClick={handleCTA}
            className="px-10 py-6 text-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-bold"
            data-testid="cta-recipes"
          >
            <BookOpen className="w-5 h-5 mr-2" />
            Créer ma recette travaux
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <p className="text-zinc-600 text-xs mt-4">
            Gratuit • Sans engagement • Résultat immédiat
          </p>
        </motion.div>
      </section>

    </div>
  );
}
